import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

export type ConfirmVariant = "default" | "danger" | "warning";

export interface ConfirmModalProps {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const accent: Record<ConfirmVariant, { color: "error" | "warning" | "primary"; icon: React.ReactNode; ring: string }> = {
  danger: { color: "error", icon: <DeleteOutlineIcon />, ring: "rgba(220,38,38,0.12)" },
  warning: { color: "warning", icon: <WarningAmberRoundedIcon />, ring: "rgba(217,119,6,0.12)" },
  default: { color: "primary", icon: <HelpOutlineIcon />, ring: "rgba(37,99,235,0.12)" },
};

/**
 * Presentational confirmation dialog. Usually not used directly — drive it
 * imperatively through `useConfirm()`. Escape / backdrop click trigger onCancel
 * (blocked while loading). Cancel is the default-focused, safe action.
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Yes, Proceed",
  cancelText = "Cancel",
  variant = "default",
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const a = accent[variant] ?? accent.default;

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Escape key + click-outside close the modal — but never mid-request.
        if (loading) return;
        if (reason === "backdropClick" || reason === "escapeKeyDown") onCancel();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: a.ring,
            color: `${a.color}.main`,
            flexShrink: 0,
          }}
        >
          {a.icon}
        </Box>
        <Box sx={{ fontWeight: 700, fontSize: 18 }}>{title}</Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: "text.secondary", whiteSpace: "pre-line" }}>
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={loading}
          autoFocus
          sx={{ textTransform: "none", borderColor: "divider", color: "text.secondary" }}
        >
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color={a.color}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none", minWidth: 120 }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmModal;
